import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook for counting up numbers with animation
 * @param {number} end - Target number to count to
 * @param {number} duration - Animation duration in milliseconds
 * @param {string} suffix - Suffix to add (like '+' or '%')
 * @param {boolean} start - Trigger to start counting
 * @returns {string} - Formatted number with suffix
 */
export const useCountUp = (end, duration = 2000, suffix = '', start = false) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (start && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime = null;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        // Easing function for smooth animation
        const easeOutQuart = (x) => 1 - Math.pow(1 - x, 4);
        
        const easedProgress = easeOutQuart(progress);
        setCount(Math.floor(end * easedProgress));

        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(end);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [start, end, duration]);

  return `${count}${suffix}`;
};
